import { useEffect } from "react";
import { Checkbox, Typography } from "@commonlibs";
import clsx from "clsx";
import TabsModalFooter from "ui/common/components/feedback/TabsModal/TabsModalFooter/TabsModalFooter";
import useTranslate from "ui/locale/hooks/useTranslate";
import Form from "ui/common/components/input/Form/Form";
import useTabsState from "ui/common/components/feedback/TabsModal/hooks/useTabsState";
import { UpdateConversionRateState } from "./UpdateConversionRateModal.types";
import usePointConversionRate from "ui/point/query/usePointConversionRate";
import useCloseTabModal from "ui/common/components/feedback/TabsModal/hooks/useCloseTabModal";
import useUpdateConversionRate from "features/points/hooks/useUpdateConversionRate";
import RateBalance from "ui/point/components/display/RateBalance";
import useVerifyReleaseStrategyMethod from "ui/release-strategy/hooks/useVerifyReleaseStrategyMethod";
import { Operations } from "ui/admins/operations.types";
import {
    ConfirmUpdateConversionRateTabRoot,
    WarningBox,
    WarningIcon,
    InfoBox,
    ConversionInputsContainer,
    InputRow,
    InputContainer,
    EditableInputField,
    RateBalanceContainer,
    CheckboxContainer,
} from "./ConfirmUpdateConversionRateTab.styles";

export interface ConfirmUpdateConversionRateTabProps {
    className?: string;
    style?: React.CSSProperties;
}

function ConfirmUpdateConversionRateTab({ className, ...rest }: Readonly<ConfirmUpdateConversionRateTabProps>) {
    const translate = useTranslate();
    const closeModal = useCloseTabModal();
    const { data: currentConversionRate = 0 } = usePointConversionRate();
    const [{ conversionRate: newConversionRate }, setState] = useTabsState<UpdateConversionRateState>();

    const { mutate, isLoading } = useUpdateConversionRate({ onSettled: closeModal });

    const { verifyReleaseMethod, isValidatingReleaseStrategy } = useVerifyReleaseStrategyMethod({
        onSuccess: closeModal,
        isLoading,
    });

    useEffect(() => {
        if (!newConversionRate && currentConversionRate > 0) {
            setState({ conversionRate: Math.floor(currentConversionRate) });
        }
    }, [currentConversionRate, newConversionRate, setState]);

    function handleSubmit() {
        const executeAction = (...args: unknown[]) => {
            const [conversionData] = args as [{ conversionRate: number }];
            mutate(conversionData);
        };

        verifyReleaseMethod({
            originalFn: executeAction,
            originalArgs: [{ conversionRate: newConversionRate }],
            operationTypeId: Operations.POINTS_CONVERSION_RATE_UPDATE,
        });
    }

    function handleNewRateChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = Math.floor(Number(event.target.value));
        if (!Number.isNaN(value) && value > 0) {
            setState({ conversionRate: value });
        }
    }

    return (
        // skipcq: JS-0415
        <Form className={clsx("update-conversion-rate-tab", className)} onSubmit={handleSubmit} {...rest}>
            <ConfirmUpdateConversionRateTabRoot>
                <WarningBox>
                    <WarningIcon />
                    <Typography variant="body2Strong" style={{ color: "#856404", fontSize: "14px" }}>
                        {translate("updateConversionRateAlert")}
                    </Typography>
                </WarningBox>

                <InfoBox>
                    <Typography variant="body2Regular" style={{ color: "#495057", fontSize: "14px", lineHeight: "1.5" }}>
                        {translate("converionRateUpdateText")}
                    </Typography>
                </InfoBox>

                <ConversionInputsContainer>
                    <InputRow>
                        <InputContainer>
                            <Typography variant="body2Strong" style={{ color: "#495057", fontSize: "14px", marginBottom: "8px" }}>
                                {translate("currentConversionRate")}
                            </Typography>
                            <RateBalanceContainer>
                                <RateBalance balance={Number(currentConversionRate)} variant="body2Regular" />
                            </RateBalanceContainer>
                        </InputContainer>
                        <InputContainer>
                            <Typography variant="body2Strong" style={{ color: "#495057", fontSize: "14px", marginBottom: "8px" }}>
                                {translate("newConversionRate")}
                            </Typography>
                            <EditableInputField
                                type="number"
                                value={newConversionRate || Math.floor(currentConversionRate) || ""}
                                onChange={handleNewRateChange}
                                placeholder="Ingrese nueva tasa"
                                min="1"
                                step="1"
                            />
                        </InputContainer>
                    </InputRow>
                </ConversionInputsContainer>

                <CheckboxContainer>
                    <Checkbox
                        name="accepted"
                        required
                        label={
                            <Typography variant="body2Regular" style={{ color: "#495057", fontSize: "14px", lineHeight: "1.4" }}>
                                {translate("understandConversionRateImplications")}
                            </Typography>
                        }
                    />
                </CheckboxContainer>
            </ConfirmUpdateConversionRateTabRoot>

            <TabsModalFooter mainType="submit" mainLabel={translate("confirm")} isLoading={isLoading || isValidatingReleaseStrategy} />
        </Form>
    );
}

export default ConfirmUpdateConversionRateTab;
